import { useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Confirm, useNotify, usePermissions, useRecordContext, useRefresh } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { MADRASA_SETTINGS_EDIT } from "../shared/madrasa-permissions";

type AcademicYearRecord = { id: string; name: string; isCurrent: boolean; isClosed: boolean };
type ActionKey = "set-current" | "close" | "reopen";

const CONFIRM_COPY: Record<ActionKey, (name: string) => { title: string; content: string }> = {
  "set-current": (name) => ({
    title: `Make ${name} the current year?`,
    content:
      "New enrollments and fee collection default to the current academic year. The previous current year stays as it is.",
  }),
  close: (name) => ({
    title: `Close ${name}?`,
    content:
      "A closed year is read-only: it can no longer be edited or made current. You can reopen it later.",
  }),
  reopen: (name) => ({
    title: `Reopen ${name}?`,
    content: "The year becomes editable again.",
  }),
};

const SUCCESS_COPY: Record<ActionKey, string> = {
  "set-current": "Current academic year updated",
  close: "Academic year closed",
  reopen: "Academic year reopened",
};

/** Row actions for the Academic Years list (each state change is confirmed first). */
export function AcademicYearActions() {
  const record = useRecordContext<AcademicYearRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [pending, setPending] = useState<ActionKey | null>(null);
  const [busy, setBusy] = useState(false);

  if (!record || !hasPermission(permissions, MADRASA_SETTINGS_EDIT)) {
    return null;
  }

  const run = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await callApi({ location: `academicYears/${record.id}/${pending}`, method: "POST" });
      notify(SUCCESS_COPY[pending], { type: "success" });
      refresh();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Action failed"), { type: "error" });
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const copy = pending ? CONFIRM_COPY[pending](record.name) : null;

  return (
    <>
      <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
        {!record.isClosed && !record.isCurrent && (
          <Button size="small" onClick={() => setPending("set-current")}>
            Set as current
          </Button>
        )}
        {!record.isClosed && !record.isCurrent && (
          <Button size="small" color="inherit" onClick={() => setPending("close")}>
            Close
          </Button>
        )}
        {record.isClosed && (
          <Button size="small" onClick={() => setPending("reopen")}>
            Reopen
          </Button>
        )}
      </Stack>
      <Confirm
        isOpen={pending !== null}
        loading={busy}
        title={copy?.title ?? ""}
        content={copy?.content ?? ""}
        onConfirm={run}
        onClose={() => setPending(null)}
      />
    </>
  );
}
